import React, {useState} from 'react';
import {useTranslation} from 'react-i18next';
import {Link} from 'react-router-dom';
import {ActivityLogDetail} from './ActivityLogDetail';
import {resolveTitle, hasRenderableDetail} from './detail_renderers/registry';

const CATEGORY_ICONS = {
    BUDGET: 'fa-dollar-sign',
    CONFIGURATION: 'fa-cog',
    ACCOUNTS: 'fa-link',
    TEAM: 'fa-users',
    ALERTS: 'fa-bell',
    INTEGRATIONS: 'fa-plug',
    REPORTS: 'fa-file-alt',
    COMMENT: 'fa-comment',
    STATUS: 'fa-toggle-on',
    AGENT: 'fa-robot',
    EXTERNAL: 'fa-handshake',
    MEMORY_OPS: 'fa-brain',
    PLATFORM_CHANGE: 'fa-sliders-h',
};

const EVENT_TYPE_ICONS = {
    DAILY_LIMIT_EXCEEDED: 'fa-exclamation-triangle',
    ALERT_TRIGGERED: 'fa-bolt',
    // «Avisarle al equipo» desde «Inversión sin control» (nombre de FA5: el webfont es 5.2.0).
    UNCONTROLLED_SPEND_FLAGGED: 'fa-bullhorn',
    MEETING_HELD: 'fa-handshake',
    DECISION_TAKEN: 'fa-gavel',
    COMMITMENT_MADE: 'fa-flag',
    EMAIL_EXCHANGED: 'fa-envelope',
    MEMORY_DOC_UPLOADED: 'fa-upload',
    MEMORY_DOC_RETRIED: 'fa-redo',
    MEMORY_DOC_REFRESHED: 'fa-sync-alt',
    MEMORY_DOC_RELABELED: 'fa-tag',
    MEMORY_DOC_DELETED: 'fa-trash',
    MEMORY_FACT_CREATED: 'fa-plus',
    MEMORY_FACT_APPROVED: 'fa-check',
    MEMORY_FACT_REJECTED: 'fa-times',
    MEMORY_FACT_EDITED: 'fa-pen',
    MEMORY_FACT_SHARED: 'fa-share',
    MEMORY_FACT_CONFIRMED: 'fa-check-double',
    MEMORY_FACT_AUTO_ARCHIVED: 'fa-archive',
    MEMORY_FACT_AUTO_MERGED: 'fa-object-group',
    MEMORY_FACT_MERGED: 'fa-object-group',
    MEMORY_FACT_RESTORED: 'fa-undo',
    // Cambios hechos DIRECTO en la plataforma (Google/Meta)
    PLATFORM_BUDGET_CHANGED: 'fa-dollar-sign',
    PLATFORM_STATUS_CHANGED: 'fa-toggle-on',
    PLATFORM_BID_CHANGED: 'fa-gavel',
    PLATFORM_TARGETING_CHANGED: 'fa-bullseye',
    PLATFORM_CREATIVE_CHANGED: 'fa-image',
    PLATFORM_STRUCTURE_CHANGED: 'fa-sitemap',
    PLATFORM_SYSTEM_EVENT: 'fa-robot',
    PLATFORM_OTHER: 'fa-sliders-h',
};

// Badge de canal para los cambios de plataforma (source_platform en el detail).
const CHANNEL_BADGES = {
    GOOGLE: {labelKey: 'activity_log.channel_google', className: '-google'},
    FACEBOOK: {labelKey: 'activity_log.channel_meta', className: '-facebook'},
};

export function ActivityLogEntry({entry, clientId}) {
    const {t, i18n} = useTranslation();
    const [expanded, setExpanded] = useState(false);

    const icon = EVENT_TYPE_ICONS[entry.event_type] || CATEGORY_ICONS[entry.category] || 'fa-circle';
    const categoryClass = entry.category ? entry.category.toLowerCase() : '';
    // El ícono dice QUÉ pasó; esta clase dice QUIÉN lo hizo (el mockup `_9` pinta la persona, la
    // plataforma, la alerta y la memoria de colores distintos). El balde lo decide el backend
    // —`activity_log_actors.py`, el mismo que arma el filtro «Quién»— así que una fila nunca puede
    // salir pintada de un color que el filtro cuenta en otro. Sin `actor` (payload viejo, o un
    // deploy a mitad de camino) no se agrega nada y queda el color de categoría de siempre.
    const actorClass = entry.actor && entry.actor.kind ? entry.actor.kind.toLowerCase() : '';
    // El toggle "Ver detalle" solo aparece si el registry tiene algo renderizable para
    // esta entrada (un detail vacío o solo con campos técnicos no genera panel).
    const hasDetail = hasRenderableDetail(entry);
    const hasComment = !!entry.comment_text;

    const description = resolveTitle(entry, t);

    // Quién lo hizo, en la fila de meta. Sale de `actor` y no de `performed_by`: ese FK viene vacío
    // en todo cambio traído de una plataforma, así que una fila cuyo título dice "Pablo Qdiez
    // cambió…" no mostraba autor por ningún lado — y con el filtro «Quién» puesto no había forma de
    // comprobar que estuviera filtrando bien.
    //
    // Dos silencios a propósito. «Sistema Dashbo» no se escribe: el badge de categoría ya lo dice y
    // repetirlo en cada alerta es ruido. Y tampoco se repite el actor cuando el TÍTULO ya arranca
    // con él, que es el caso de casi todos los cambios de plataforma (el mapper compone "Meta ·
    // {actor} {qué cambió}" y la fila le saca el prefijo del canal): decir "Pablo Qdiez cambió la
    // segmentación… · por Pablo Qdiez" no agrega nada.
    //
    // El nombre se busca EN CUALQUIER PARTE del título, no sólo al principio: los cambios de
    // plataforma arrancan con el actor ("Pablo Qdiez cambió…") pero un comentario dice
    // "Comentario agregado por Fede Serbin", con el nombre al final — y ahí el autor salía dos
    // veces en la misma fila.
    const actorLabel = (entry.actor && entry.actor.kind !== 'DASHBO_SYSTEM' && entry.actor.label) || null;
    const showActor = !!actorLabel
        && !(typeof description === 'string' && description.indexOf(actorLabel) !== -1);

    // Para los cambios de plataforma el dato relevante es cuándo ocurrió EN la plataforma
    // (event_datetime), no cuándo lo ingerimos. Para el resto, event_datetime viene null → created_on.
    // Timestamps corruptos o ausentes → sin fecha (nunca "Invalid Date" ni 1/1/1970).
    const rawTimestamp = entry.event_datetime || entry.created_on;
    const timestamp = rawTimestamp ? new Date(rawTimestamp) : new Date(NaN);
    const hasTimestamp = Number.isFinite(timestamp.getTime());
    const dateStr = hasTimestamp ? timestamp.toLocaleDateString(i18n.language) : '';
    const timeFormat = {hour: '2-digit', minute: '2-digit'};
    let timeStr = hasTimestamp ? timestamp.toLocaleTimeString(i18n.language, timeFormat) : '';
    // Grupo colapsado (ráfaga de alertas / episodio de cambios): el horario es un RANGO
    // (primer tick – último tick), no un instante.
    if (entry.occurrences > 1 && entry.first_seen && entry.last_seen) {
        const firstDate = new Date(entry.first_seen);
        const lastDate = new Date(entry.last_seen);
        if (Number.isFinite(firstDate.getTime()) && Number.isFinite(lastDate.getTime())) {
            const first = firstDate.toLocaleTimeString(i18n.language, timeFormat);
            const last = lastDate.toLocaleTimeString(i18n.language, timeFormat);
            if (first !== last) timeStr = `${first} – ${last}`;
        }
    }

    // Cambios de plataforma: badge de canal (Google/Meta) + nº de ocurrencias si vino colapsado.
    const channel = entry.detail && CHANNEL_BADGES[entry.detail.source_platform];
    const occurrences = entry.occurrences > 1 ? entry.occurrences : null;
    // Líneas de Control afectadas (una campaña suele estar en varias BUs). Para platform changes
    // viene la lista en el detail; para el resto, el FK simple entry.business_unit.
    // Payloads históricos pueden traer cualquier cosa acá (string, lista de ids) — solo se
    // pintan los ítems con nombre.
    const rawBusinessUnits = entry.detail && entry.detail.business_units;
    const businessUnits = (Array.isArray(rawBusinessUnits) ? rawBusinessUnits : [])
        .filter(bu => bu && bu.name);

    return (
        <div className="activity-log-entry">
            <div className={`entry-icon -${categoryClass}${actorClass ? ` -actor-${actorClass}` : ''}`}>
                <i className={`fas ${icon}`}/>
            </div>

            <div className="entry-content">
                <div className="entry-header">
                    <div className="entry-title-group">
                        <span className="entry-description">{description}</span>
                        {businessUnits.length > 0
                            ? businessUnits.map(bu => (
                                <span key={bu.id} className="entry-bu-tag">{bu.name}</span>))
                            : entry.business_unit && (
                                <span className="entry-bu-tag">{entry.business_unit.name}</span>)}
                    </div>
                    <span className="entry-timestamp">
                        {dateStr} {timeStr}
                    </span>
                </div>

                <div className="entry-meta">
                    {categoryClass && (
                        <span className={`entry-category-badge -${categoryClass}`}>
                            {t('activity_log.category_' + categoryClass)}
                        </span>
                    )}
                    {channel && (
                        <span className={`entry-channel-badge ${channel.className}`}>
                            {t(channel.labelKey)}
                        </span>
                    )}
                    {occurrences && (
                        <span className="entry-occurrences"
                              title={t('activity_log.occurrences_tooltip', {count: occurrences})}>
                            ×{occurrences}
                        </span>
                    )}
                    {showActor && (
                        <span>{t('activity_log.by')} {entry.actor.label}</span>
                    )}
                    {!entry.actor && entry.performed_by && (
                        <span>
                            {t('activity_log.by')} {entry.performed_by.full_name}
                        </span>
                    )}
                    {entry.category === 'AGENT' && entry.detail && entry.detail.session_id && clientId && (
                        <Link to={`/client/${clientId}/agent/${entry.detail.session_id}`} className="entry-session-link">
                            {t('activity_log.open_agent_session')}
                        </Link>
                    )}
                    {/* El toggle vive en la fila de meta (antes ocupaba una línea entera por
                        entrada — en un feed denso era puro alto desperdiciado). */}
                    {hasDetail && (
                        <button className="entry-detail-toggle-inline" onClick={() => setExpanded(!expanded)}>
                            {expanded ? t('activity_log.hide_detail') : t('activity_log.show_detail')}
                        </button>
                    )}
                </div>

                {hasComment && (
                    <div className={`entry-comment -${categoryClass}`}
                         dangerouslySetInnerHTML={{__html: entry.comment_text}}/>
                )}

                {hasDetail && expanded && (
                    <div className="entry-detail-content">
                        <ActivityLogDetail entry={entry}/>
                    </div>
                )}
            </div>
        </div>
    );
}
